/**
 * API Error Parser
 *
 * Utility to parse and extract user-friendly error messages from API responses
 */

interface ValidationError {
  msg?: string;
  message?: string;
  type?: string;
  loc?: string[];
  input?: any;
  url?: string;
}

interface ApiErrorResponse {
  response?: {
    data?: {
      detail?: string | ValidationError[] | any;
      message?: string;
    };
    status?: number;
  };
  message?: string;
}

/**
 * Parse API error and return user-friendly message
 */
export const parseApiError = (
  error: unknown,
  defaultMessage: string = 'An error occurred. Please try again.'
): string => {
  const apiError = error as ApiErrorResponse;

  // Handle detail field (can be string, array, or object)
  if (apiError.response?.data?.detail) {
    const detail = apiError.response.data.detail;

    // Handle array of validation errors (FastAPI/Pydantic format)
    if (Array.isArray(detail)) {
      const messages = detail
        .map(err => err.msg || err.message || 'Validation error')
        .filter(Boolean);

      return messages.length > 0 ? messages.join(', ') : defaultMessage;
    }

    // Handle string detail
    if (typeof detail === 'string') {
      return detail;
    }

    // Handle object detail (generic)
    if (typeof detail === 'object') {
      // Try to extract message from object
      if ('message' in detail && typeof detail.message === 'string') {
        return detail.message;
      }
      if ('msg' in detail && typeof detail.msg === 'string') {
        return detail.msg;
      }
      return 'Validation error occurred';
    }
  }

  // Handle message field
  if (apiError.response?.data?.message) {
    return apiError.response.data.message;
  }

  // Handle axios error message
  if (apiError.message) {
    return apiError.message;
  }

  // Fallback to default message
  return defaultMessage;
};

/**
 * Parse API error and return array of error messages
 */
export const parseApiErrors = (
  error: unknown,
  defaultMessage: string = 'An error occurred. Please try again.'
): string[] => {
  const apiError = error as ApiErrorResponse;

  // Handle detail field
  if (apiError.response?.data?.detail) {
    const detail = apiError.response.data.detail;

    // Handle array of validation errors
    if (Array.isArray(detail)) {
      const messages = detail
        .map(err => err.msg || err.message || 'Validation error')
        .filter(Boolean);

      return messages.length > 0 ? messages : [defaultMessage];
    }

    // Handle string detail
    if (typeof detail === 'string') {
      return [detail];
    }
  }

  // Handle message field
  if (apiError.response?.data?.message) {
    return [apiError.response.data.message];
  }

  // Handle axios error message
  if (apiError.message) {
    return [apiError.message];
  }

  // Fallback to default message
  return [defaultMessage];
};

/**
 * Get HTTP status code from error
 */
export const getErrorStatus = (error: unknown): number | null => {
  const apiError = error as ApiErrorResponse;
  return apiError.response?.status || null;
};

/**
 * Check if error is a validation error (422)
 */
export const isValidationError = (error: unknown): boolean => {
  return getErrorStatus(error) === 422;
};

/**
 * Check if error is unauthorized (401)
 */
export const isUnauthorizedError = (error: unknown): boolean => {
  return getErrorStatus(error) === 401;
};

/**
 * Check if error is forbidden (403)
 */
export const isForbiddenError = (error: unknown): boolean => {
  return getErrorStatus(error) === 403;
};

/**
 * Check if error is not found (404)
 */
export const isNotFoundError = (error: unknown): boolean => {
  return getErrorStatus(error) === 404;
};
